import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Typography,
  Switch,
  Divider,
  TextField,
  Alert,
  AlertTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
import { Label } from "app/pages/presentation/module/form/types";
import {
  CATEGORY_PRODUCTS_ACTIONS,
  CATEGORY_PRODUCTS_DISPLAY_TYPES,
  DSOK_LAST_CATEGORY_TYPES,
} from "app/utils/constants/settings";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ClearIcon from "@mui/icons-material/Clear";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder";

const FIELD_NAME = "CATEGORIES_FIELD";

const SmartkioskCategories = ({
  handleChange,
  expanded,
  hideSectionTitle = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const [optionsData, setOptionsData] = useState({});
  const [categoriesData, setCategoriesData] = useState({});
  const [optionsJsonText, setOptionsJsonText] = useState({});
  const [categoriesJsonText, setCategoriesJsonText] = useState({});
  const defaultCategoriesData = {
    default: "all",
    levels: { 1: "all", 2: "all" },
    columns: { 1: 3, 2: 3 },
    searchLevels: { 1: false, 2: false },
  };
  const [selectedCategoryDisplaySettings, setSelectedCategoryDisplaySettings] =
    useState("1");

  const { showDialog, hideDialog } = useJumboDialog();

  const categoriesDisplaySettingsKeys = Object.keys(
    values.settings.smartkiosk.categoriesDisplaySettings || {},
  );

  const handleAddCategoryDisplaySettings = () => {
    setFieldValue("settings.smartkiosk.categoriesDisplaySettings", {
      ...values.settings.smartkiosk.categoriesDisplaySettings,
      [categoriesDisplaySettingsKeys.length + 1]: {},
    });
  };

  const handleDeleteCategoryDisplaySettings = (categoryDisplaySettingsKey) => {
    const newCategories = {};
    let newIndex = 1;
    categoriesDisplaySettingsKeys.forEach((key) => {
      if (key !== categoryDisplaySettingsKey) {
        newCategories[newIndex] =
          values.settings.smartkiosk.categoriesDisplaySettings[key];
        newIndex++;
      }
    });
    setFieldValue(
      "settings.smartkiosk.categoriesDisplaySettings",
      newCategories,
    );
    setSelectedCategoryDisplaySettings(Object.keys(newCategories)[0] || "1");
    hideDialog();
  };

  const handleOptionsChange = (value, categoryDisplaySettingsKey) => {
    setOptionsData(value);
    setFieldValue(
      `settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.options`,
      value,
    );
  };

  const handleCategoriesChange = (value, categoryDisplaySettingsKey) => {
    setCategoriesData(value);
    setFieldValue(
      `settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.categories`,
      value,
    );
  };

  const content = (
    <Grid item xs={12} md={12} lg={12}>
      <AccordionDetails>
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          spacing={1}
          px={2}
        >
          <TabContext value={selectedCategoryDisplaySettings}>
            <Stack width="100%">
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  width: "100%",
                }}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <TabList
                  onChange={(_, val) => setSelectedCategoryDisplaySettings(val)}
                  sx={{ width: "100%" }}
                >
                  {categoriesDisplaySettingsKeys.map((item) => {
                    return (
                      <Tab
                        key={item}
                        label={
                          <Stack direction="row" sx={{ alignItems: "center" }}>
                            {values.settings.smartkiosk
                              .categoriesDisplaySettings?.[item]?.code ?? item}
                            <IconButton
                              onClick={() =>
                                showDialog({
                                  variant: "confirm",
                                  title: t("widgets.confirmDialog.areYouSure"),
                                  onYes: () =>
                                    handleDeleteCategoryDisplaySettings(item),
                                  onNo: () => hideDialog(),
                                })
                              }
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                        value={item}
                      />
                    );
                  })}
                </TabList>

                <IconButton onClick={handleAddCategoryDisplaySettings}>
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </Box>
              {!categoriesDisplaySettingsKeys?.length ? (
                <JumboListNoDataPlaceholder />
              ) : (
                <Box sx={{ width: "100%" }}>
                  {categoriesDisplaySettingsKeys.map(
                    (categoryDisplaySettingsKey) => {
                      return (
                        <TabPanel
                          key={`tab_${categoryDisplaySettingsKey}`}
                          value={categoryDisplaySettingsKey}
                          sx={{
                            pb: 1,
                            pr: 0,
                            pl: 0,
                            width: "100%",
                          }}
                        >
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={12} lg={2}>
                              <Div sx={{ mb: 1, mx: 0 }}>
                                <JumboTextField
                                  fullWidth
                                  InputLabelProps={{ required: false }}
                                  name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.code`}
                                  label={
                                    <Label
                                      text={t(
                                        "pages.dsokDetails.categories.categoriesDisplaySettings.code",
                                      )}
                                    />
                                  }
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={1}>
                              <Div sx={{ mb: 1, mx: 0 }}>
                                <JumboTextField
                                  fullWidth
                                  type="number"
                                  InputLabelProps={{ required: false }}
                                  name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.columns`}
                                  label={
                                    <Label
                                      text={t(
                                        "pages.dsokDetails.categories.categoriesDisplaySettings.columns",
                                      )}
                                    />
                                  }
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={2}>
                              <FormControl sx={{ mt: 0, mb: 0 }} fullWidth>
                                <JumboTextField
                                  select
                                  required
                                  InputLabelProps={{ required: false }}
                                  name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.lastCategoryType`}
                                  label={t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.lastCategoryType",
                                  )}
                                >
                                  {DSOK_LAST_CATEGORY_TYPES.map(
                                    (type, index) => {
                                      return (
                                        <MenuItem key={index} value={type.code}>
                                          {type.name}
                                        </MenuItem>
                                      );
                                    },
                                  )}
                                </JumboTextField>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={12} lg={2}>
                              <Div sx={{ mb: 1, mx: 0 }}>
                                <JumboTextField
                                  fullWidth
                                  type="number"
                                  InputLabelProps={{ required: false }}
                                  name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.lastCategoryColumns`}
                                  label={
                                    <Label
                                      text={t(
                                        "pages.dsokDetails.categories.categoriesDisplaySettings.lastCategoryColumns",
                                      )}
                                    />
                                  }
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                              <Alert severity="info">
                                <AlertTitle>
                                  {t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.options.sidebar.help.title",
                                  )}
                                </AlertTitle>
                                {t(
                                  "pages.dsokDetails.categories.categoriesDisplaySettings.options.sidebar.help.description",
                                ) +
                                  "{" +
                                  '  "sidebar": {' +
                                  '    "defaultCategory": {' +
                                  '      "condition": "starts-with",' +
                                  '      "value": "valdo"' +
                                  "    }" +
                                  "  }" +
                                  "}"}
                              </Alert>
                              {t(
                                "pages.dsokDetails.categories.categoriesDisplaySettings.options.sidebar.help.note",
                              )}
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={6}
                                  value={
                                    optionsJsonText[
                                      categoryDisplaySettingsKey
                                    ] ??
                                    (() => {
                                      const raw =
                                        values?.settings?.smartkiosk
                                          ?.categoriesDisplaySettings?.[
                                          categoryDisplaySettingsKey
                                        ]?.options ?? null;
                                      try {
                                        return raw === null || raw === undefined
                                          ? ""
                                          : typeof raw === "object"
                                            ? JSON.stringify(raw, null, 2)
                                            : String(raw);
                                      } catch {
                                        return String(raw ?? "");
                                      }
                                    })()
                                  }
                                  onChange={(e) => {
                                    const text = e.target.value;
                                    setOptionsJsonText((prev) => ({
                                      ...prev,
                                      [categoryDisplaySettingsKey]: text,
                                    }));
                                    try {
                                      const parsed =
                                        text.trim() === ""
                                          ? null
                                          : JSON.parse(text);
                                      if (
                                        parsed === null ||
                                        typeof parsed === "object"
                                      ) {
                                        handleOptionsChange(
                                          parsed,
                                          categoryDisplaySettingsKey,
                                        );
                                        setOptionsJsonText((prev) => {
                                          const next = { ...prev };
                                          delete next[
                                            categoryDisplaySettingsKey
                                          ];
                                          return next;
                                        });
                                      }
                                    } catch {
                                      // Invalid JSON: keep text in state
                                    }
                                  }}
                                  placeholder='{"sidebar": {"defaultCategory": {"condition": "starts-with", "value": "valdo"}}}'
                                  sx={{
                                    "& textarea": {
                                      fontFamily: "monospace",
                                      fontSize: "0.875rem",
                                    },
                                  }}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                              <Alert severity="info">
                                <AlertTitle>
                                  {t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.views.categories.title",
                                  )}
                                </AlertTitle>
                                {t(
                                  "pages.dsokDetails.categories.categoriesDisplaySettings.views.categories.description",
                                )}
                              </Alert>
                              <Alert severity="info">
                                {t(
                                  "pages.dsokDetails.categories.categoriesDisplaySettings.views.categories.note",
                                )}
                              </Alert>
                              <Div sx={{ mt: 1, mb: 1 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={6}
                                  value={
                                    categoriesJsonText[
                                      categoryDisplaySettingsKey
                                    ] ??
                                    (() => {
                                      const raw =
                                        values?.settings?.smartkiosk
                                          ?.categoriesDisplaySettings?.[
                                          categoryDisplaySettingsKey
                                        ]?.views?.categories ||
                                        defaultCategoriesData;
                                      try {
                                        return typeof raw === "object"
                                          ? JSON.stringify(raw, null, 2)
                                          : String(raw ?? "");
                                      } catch {
                                        return String(raw ?? "");
                                      }
                                    })()
                                  }
                                  onChange={(e) => {
                                    const text = e.target.value;
                                    setCategoriesJsonText((prev) => ({
                                      ...prev,
                                      [categoryDisplaySettingsKey]: text,
                                    }));
                                    try {
                                      const parsed =
                                        text.trim() === ""
                                          ? defaultCategoriesData
                                          : JSON.parse(text);
                                      if (
                                        parsed !== null &&
                                        typeof parsed === "object"
                                      ) {
                                        handleCategoriesChange(
                                          parsed,
                                          categoryDisplaySettingsKey,
                                        );
                                        setCategoriesJsonText((prev) => {
                                          const next = { ...prev };
                                          delete next[
                                            categoryDisplaySettingsKey
                                          ];
                                          return next;
                                        });
                                      }
                                    } catch {
                                      // Invalid JSON: keep text in state
                                    }
                                  }}
                                  placeholder={JSON.stringify(
                                    defaultCategoriesData,
                                    null,
                                    2,
                                  )}
                                  sx={{
                                    "& textarea": {
                                      fontFamily: "monospace",
                                      fontSize: "0.875rem",
                                    },
                                  }}
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                              <Typography
                                sx={{
                                  width: "33%",
                                  flexShrink: 0,
                                  margin: 2,
                                }}
                              >
                                {t("pages.dsokDetails.products.title")}
                              </Typography>
                              <Divider />
                            </Grid>
                            <Grid item xs={12} md={4} lg={2}>
                              <FormControl sx={{ mt: 0, mb: 0 }} fullWidth>
                                <JumboTextField
                                  select
                                  required
                                  InputLabelProps={{ required: false }}
                                  name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.default`}
                                  defaultValue="all"
                                  label={t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.views.products.default",
                                  )}
                                >
                                  {CATEGORY_PRODUCTS_DISPLAY_TYPES.map(
                                    (type, index) => {
                                      return (
                                        <MenuItem key={index} value={type.code}>
                                          {type.name}
                                        </MenuItem>
                                      );
                                    },
                                  )}
                                </JumboTextField>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={12} lg={1}>
                              <Div sx={{ mb: 1, mx: 0 }}>
                                <JumboTextField
                                  fullWidth
                                  type="number"
                                  InputLabelProps={{ required: false }}
                                  name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.columns`}
                                  label={
                                    <Label
                                      text={t(
                                        "pages.dsokDetails.categories.categoriesDisplaySettings.views.products.columns",
                                      )}
                                    />
                                  }
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={2}>
                              <FormControl sx={{ mt: 0, mb: 0 }} fullWidth>
                                <JumboTextField
                                  select
                                  required
                                  InputLabelProps={{ required: false }}
                                  name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.action`}
                                  defaultValue="all"
                                  label={t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.views.products.action",
                                  )}
                                >
                                  {CATEGORY_PRODUCTS_ACTIONS.map(
                                    (type, index) => {
                                      return (
                                        <MenuItem key={index} value={type.code}>
                                          {type.name}
                                        </MenuItem>
                                      );
                                    },
                                  )}
                                </JumboTextField>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}></Grid>
                            <Grid item xs={12} md={4} lg={2}>
                              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Field
                                      name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.search`}
                                      component={Switch}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.search`,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  }
                                  label={t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.views.products.search",
                                  )}
                                  checked={
                                    values.settings?.smartkiosk
                                      ?.categoriesDisplaySettings?.[
                                      categoryDisplaySettingsKey
                                    ]?.views?.products?.search || false
                                  }
                                />
                              </Div>
                            </Grid>
                            <Grid item xs={12} md={4} lg={2}>
                              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Field
                                      name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.showAddToCartAction`}
                                      component={Switch}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.showAddToCartAction`,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  }
                                  label={t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.views.products.showAddToCartAction",
                                  )}
                                  checked={
                                    values.settings?.smartkiosk
                                      ?.categoriesDisplaySettings?.[
                                      categoryDisplaySettingsKey
                                    ]?.views?.products?.showAddToCartAction ||
                                    false
                                  }
                                />
                              </Div>
                            </Grid>

                            <Grid item xs={12} md={4} lg={2}>
                              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Field
                                      name={`settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.showPrice`}
                                      component={Switch}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `settings.smartkiosk.categoriesDisplaySettings.${categoryDisplaySettingsKey}.views.products.showPrice`,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  }
                                  label={t(
                                    "pages.dsokDetails.categories.categoriesDisplaySettings.views.products.showPrice",
                                  )}
                                  checked={
                                    values.settings?.smartkiosk
                                      ?.categoriesDisplaySettings?.[
                                      categoryDisplaySettingsKey
                                    ]?.views?.products?.showPrice || false
                                  }
                                />
                              </Div>
                            </Grid>
                          </Grid>
                        </TabPanel>
                      );
                    },
                  )}
                </Box>
              )}
            </Stack>
          </TabContext>
        </Stack>
      </AccordionDetails>
    </Grid>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.categories.title")}
        </Typography>
      </AccordionSummary>
      {content}
    </Accordion>
  );
};

export default SmartkioskCategories;
